import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLocale } from '../contexts/LocaleContext';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../config';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Plus, X } from 'lucide-react';
import { CONCEPT_ICONS } from '../utils/concept-icons';
import { isSystemTag } from '../utils/system-tags';
import { resourceListTagFilterHref } from '../utils/resource-list-navigation';
import {
  COLOR_RESOURCE_TAG_TAG_BG,
  COLOR_RESOURCE_TAG_TAG_TEXT,
  COLOR_RESOURCE_TAG_TAG_BORDER,
} from '../colors';
import { ProjectTagsDropdown } from './ProjectTagsDropdown';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const TAG_BADGE_STYLE = {
  backgroundColor: COLOR_RESOURCE_TAG_TAG_BG,
  color: COLOR_RESOURCE_TAG_TAG_TEXT,
  borderColor: COLOR_RESOURCE_TAG_TAG_BORDER,
};

export interface ProjectTagsFieldProps {
  projectId: string;
  tags: string[];
  onTagsChange: (tags: string[]) => void | Promise<void>;
  disabled?: boolean;
  contentClassName?: string;
  /** Shows the "add" trigger next to the label instead of inline with the tag chips. */
  compact?: boolean;
  /** Makes tag chips link to the resources list filtered by that tag. */
  linkTagsToResourcesList?: boolean;
}

export const ProjectTagsField: React.FC<ProjectTagsFieldProps> = ({
  projectId,
  tags,
  onTagsChange,
  disabled = false,
  contentClassName,
  compact = false,
  linkTagsToResourcesList = false,
}) => {
  const { t } = useLocale();
  const { token } = useAuth();
  const [availableTags, setAvailableTags] = useState<string[]>([]);
  const TagIcon = CONCEPT_ICONS.tag;

  useEffect(() => {
    if (!projectId || !token) {
      return;
    }
    const fetchTags = async (): Promise<void> => {
      try {
        const response = await fetch(`${API_BASE}/api/projects/${projectId}/tags`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (response.ok) {
          const data = await response.json();
          setAvailableTags(Array.isArray(data.tags) ? data.tags : []);
        }
      } catch {
        setAvailableTags([]);
      }
    };
    void fetchTags();
  }, [projectId, token]);

  const tagChips = (
    <>
      {tags.length === 0 ? (
        <span className="text-muted-foreground text-sm">-</span>
      ) : (
        tags.map((tag) => {
          const tagLabel = (
            <>
              <TagIcon className="h-3 w-3" />
              <span>{tag}</span>
            </>
          );

          const tagContent = linkTagsToResourcesList ? (
            <Tooltip>
              <TooltipTrigger asChild>
                <Link
                  to={resourceListTagFilterHref(projectId, tag)}
                  className="flex items-center gap-1.5 no-underline hover:underline"
                  style={{ color: 'inherit' }}
                >
                  {tagLabel}
                </Link>
              </TooltipTrigger>
              <TooltipContent side="bottom" align="start">
                {t('resource_tag_filter_tooltip')}
              </TooltipContent>
            </Tooltip>
          ) : (
            tagLabel
          );

          return (
            <Badge
              key={tag}
              variant="secondary"
              className="rounded-full px-3 py-1 flex items-center gap-1.5"
              style={TAG_BADGE_STYLE}
            >
              {tagContent}
              {!isSystemTag(tag) && !disabled && (
                <button
                  type="button"
                  aria-label={t('remove')}
                  className="rounded-full hover:bg-muted p-0.5"
                  onClick={() => {
                    void Promise.resolve(
                      onTagsChange(tags.filter((t) => t !== tag))
                    );
                  }}
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </Badge>
          );
        })
      )}
    </>
  );

  const tagsDropdown = (trigger: (params: { setIsOpen: (open: boolean) => void }) => React.ReactNode) => (
    <ProjectTagsDropdown
      selectedTags={tags}
      availableTags={availableTags}
      onTagsChange={onTagsChange}
      disabled={disabled}
      contentClassName={contentClassName}
      inputPlaceholder={t('Search tag...')}
      createLabel={(tag) => `${t('Create tag')} ${tag}`}
      trigger={trigger}
    />
  );

  if (compact) {
    return (
      <TooltipProvider>
        <div>
          <label className="mb-1.5 block text-sm font-medium">{t('Tags')}</label>
          <div className="flex flex-wrap items-center gap-2">
            {tags.length > 0 && tagChips}
            {!disabled && tagsDropdown(({ setIsOpen }) => (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-3 text-xs text-muted-foreground hover:text-foreground"
                onClick={() => setIsOpen(true)}
                disabled={disabled}
              >
                {t('add')}
              </Button>
            ))}
          </div>
        </div>
      </TooltipProvider>
    );
  }

  return (
    <TooltipProvider>
      <div>
      <label
        style={{
          fontSize: '0.875rem',
          fontWeight: 600,
          display: 'block',
          marginBottom: '0.5rem',
        }}
      >
        {t('Tags')}:
      </label>
      <div className="flex flex-wrap items-center gap-2">
        {tagChips}

        {!disabled && tagsDropdown(({ setIsOpen }) => (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-9 px-3 text-sm border-input shadow-sm"
            onClick={() => setIsOpen(true)}
            disabled={disabled}
          >
            <Plus className="h-4 w-4" />
            {t('add')}
          </Button>
        ))}
      </div>
    </div>
    </TooltipProvider>
  );
};
